import { useEffect, useState } from 'react';
import { Card, CardContent } from '../card';
import { Separator } from '../separator';
import { WorkspaceUsers } from '../workspace-users/workspace-users';
import { InviteUserButton } from '../workspace-users/components/invite-user-button/invite-user-button';
import {
  AcceptInvitationArgs,
  DeclineInvitationArgs,
  InvitationsData,
  WorkspaceSettingsProps,
} from './types';
import { supabase } from '../../../lib/supabase';
import { handleWorkspaceInvitation } from '../../../lib/api';
import { useAuthStore } from '../../../stores/authStore';
import { WorkspaceInvitations } from '../workspace-invitations/workspace-invitations';
import { WorkspaceName } from '../workspace-name/workspace-name';
import { NewWorkspace } from './components/new-workspace/new-workspace';
import { DeleteWorkspace } from './components/delete-workspace/delete-workspace';
import { LeaveWorkspace } from './components/leave-workspace/leave-workspace';
import posthog from '../../../lib/posthog';

export function WorkspaceSettings({
  authUser,
  users,
  isAdmin,
  setUsers,
  toggleUserAdmin,
}: WorkspaceSettingsProps) {
  const { fetchWorkspaceProfiles } = useAuthStore();

  const [incomingInvitations, setIncomingInvitations] = useState<
    InvitationsData[] | null
  >([]);
  const [sentInvitations, setSentInvitations] = useState<
    InvitationsData[] | null
  >([]);

  useEffect(() => {
    fetchInvitations();
  }, []);

  const fetchInvitations = async () => {
    try {
      // Fetch incoming invitations (where user is the invitee)
      const { data: incoming, error: incomingError } = await supabase
        .from('workspace_invitations')
        .select('id,invited_by,invited_email,workspace_id')
        .neq('invited_by', authUser?.email)
        .eq('invited_email', authUser?.email)
        .in('status', ['pending']);

      if (incomingError) {
        throw incomingError;
      }
      setIncomingInvitations(incoming);

      // Fetch sent invitations (where admin sent them) - only if admin
      if (isAdmin) {
        const { data: sent, error: sentError } = await supabase
          .from('workspace_invitations')
          .select('id,invited_by,invited_email,workspace_id')
          .eq('invited_by', authUser?.email)
          .eq('workspace_id', authUser?.currentWorkspace?.workspace_id)
          .in('status', ['pending']);

        if (sentError) {
          throw sentError;
        }
        setSentInvitations(sent);
      }
    } catch (err) {
      console.error('Error fetching invitations:', err);
    }
  };

  const declineInvitation = async ({
    id,
    email,
    invitedByEmail,
    workspaceId,
  }: DeclineInvitationArgs) => {
    if (!id || !email || !invitedByEmail || !workspaceId) return;
    try {
      const { error } = await supabase
        .from('workspace_invitations')
        .delete()
        .eq('id', id)
        .eq('invited_email', email)
        .eq('invited_by', invitedByEmail)
        .eq('workspace_id', workspaceId);

      if (error) {
        throw error;
      }

      fetchInvitations();
    } catch (err) {
      console.error('Error declining invitation:', err);
    }
  };

  const cancelInvitation = async ({
    id,
    email,
  }: {
    id: string;
    email: string;
  }) => {
    if (!id || !email) return;
    try {
      const { error } = await supabase
        .from('workspace_invitations')
        .delete()
        .eq('id', id)
        .eq('invited_email', email);

      if (error) {
        throw error;
      }

      fetchInvitations();
    } catch (err) {
      console.error('Error cancelling invitation:', err);
    }
  };

  const acceptInvitation = async ({
    invitationId,
    invitedByEmail,
    workspaceId,
  }: AcceptInvitationArgs) => {
    if (!invitationId || !invitedByEmail || !workspaceId) return;

    try {
      await handleWorkspaceInvitation({
        invitationId,
        invitedByEmail,
        workspaceId,
      });

      posthog.capture('workspace_invitation_accepted', {
        workspace_id: workspaceId,
      });

      supabase.auth.getSession().then(({ data: { session } }) => {
        if (session?.user) {
          fetchWorkspaceProfiles(session?.user);
        }
      });
      fetchInvitations();
    } catch (error) {
      posthog.captureException(error);
      console.error('Error accepting workspace invitation', error);
    }
  };

  const workspaceName =
    authUser?.currentWorkspace?.workspaces?.name || 'Untitled Workspace';
  const hasTeamMembers = users.length > 0;
  const hasInvitations =
    !!incomingInvitations?.length || (isAdmin && !!sentInvitations?.length);

  return (
    <Card className="rounded-[10px] border border-solid border-[#00000020] bg-prompt-safecomfantasy shadow-[0px_2px_4px_#0000001a]">
      <CardContent className="space-y-6 p-6">
        {/* Workspace Actions */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          {isAdmin ? (
            <WorkspaceName authUser={authUser} />
          ) : (
            <span className="text-sm font-medium text-prompt-safecomvulcan">
              {workspaceName}
            </span>
          )}
          <div className="flex gap-2">
            <NewWorkspace
              authUser={authUser}
              fetchWorkspaceProfiles={fetchWorkspaceProfiles}
            />
            {authUser?.currentWorkspace?.workspaces?.created_by ===
            authUser?.id ? (
              <DeleteWorkspace
                fetchWorkspaceProfiles={fetchWorkspaceProfiles}
                authUser={authUser}
              />
            ) : (
              <LeaveWorkspace
                authUser={authUser}
                fetchWorkspaceProfiles={fetchWorkspaceProfiles}
              />
            )}
          </div>
        </div>

        {hasTeamMembers && (
          <>
            <Separator className="bg-[#00000020]" />
            <div>
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h3 className="mb-1 text-sm font-semibold text-prompt-safecomvulcan">
                    Team members
                  </h3>
                  {isAdmin && (
                    <p className="text-xs text-prompt-safecomabbey">
                      Manage roles and permissions for workspace members
                    </p>
                  )}
                  {!isAdmin && (
                    <p className="text-xs text-prompt-safecomabbey">
                      View workspace team members
                    </p>
                  )}
                </div>
                {isAdmin && (
                  <div className="flex-shrink-0">
                    <InviteUserButton
                      authUser={authUser}
                      users={users}
                      fetchWorkspaceInvitations={fetchInvitations}
                    />
                  </div>
                )}
              </div>
              <WorkspaceUsers
                users={users}
                setUsers={setUsers}
                authUser={authUser}
                toggleUserAdmin={toggleUserAdmin}
                onInvitationSent={fetchInvitations}
              />
            </div>
          </>
        )}

        <>
          {hasTeamMembers && <Separator className="bg-[#00000020]" />}
          <div>
            <h3 className="mb-4 text-sm font-semibold text-prompt-safecomvulcan">
              Invitations
            </h3>
            <WorkspaceInvitations
              incomingInvitations={incomingInvitations}
              sentInvitations={sentInvitations}
              isAdmin={isAdmin}
              acceptInvitation={acceptInvitation}
              declineInvitation={declineInvitation}
              cancelInvitation={cancelInvitation}
            />
          </div>
        </>
      </CardContent>
    </Card>
  );
}
